import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import {
  getUpdatedSearchParams,
  parseSearchParams,
  getErrorResponse,
} from '../../../../services/util'
import { CustomTable } from 'components/customTable'
import { RenderDefaultActions } from '../../common'
import { usePopup } from '../../../../context/popupContext'
import { useNotificationBar } from 'context/notificationContext'
import { SmoothTransition } from 'components/pageTransition'
import { Unauthorized } from '../../../Unauthorized'
import { PagePreloader } from 'components/pagePreloader'
import { useDeleteIcons, useGetIconData } from 'hooks/useIconMaster'
import AddIconPopup from './AddIconPopup'

export const IconMasterListing = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParams: any = parseSearchParams(searchParams, 'filter')

  const { mutate: deleteIcon } = useDeleteIcons()

  const popup = usePopup()
  const notification = useNotificationBar()

  const { data, refetch, isError, isSuccess, error, isFetching } = useGetIconData(
    filterParams,
    { keepPreviousData: true }
  )

  const [isAddOpen, setIsAddOpen] = useState(false)

  const useFormHook = useForm({
    defaultValues: {
      ...filterParams,
      pagination: filterParams?.pagination || {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  })

  const responseData = useMemo(() => {
    const rawData = data?.data
    const results = Array.isArray(rawData)
      ? rawData
      : Array.isArray(rawData?.results)
        ? rawData.results
        : []

    const normalizedResults = results.map((item: any) => ({
      ...item,
      icon_name: item.icon_name ?? item.name ?? '',
      icon_url: item.icon_url ?? item.icon ?? '',
      createdAt: item.createdAt ?? item.created_at ?? null,
    }))

    return {
      results: normalizedResults,
      pages:
        rawData?.pages ??
        (rawData?.total && rawData?.pagination?.pageSize
          ? Math.ceil(rawData.total / rawData.pagination.pageSize)
          : 0),
      total: rawData?.total ?? normalizedResults.length,
    }
  }, [data])

  const onDeleteIcon = (row: any) => {
    popup.open({
      title: 'Delete Icon',
      message: (
        <>
          Are you sure you want to delete <strong>{row.icon_name}</strong>?<br />
          This action cannot be undone.
        </>
      ),
      type: 'confirm',
      onAction: (value: boolean) => {
        if (!value) return

        deleteIcon(
          { id: row.id },
          {
            onSuccess: () => {
              notification.push({
                title: 'Success',
                message: 'Icon deleted successfully',
                type: 'success',
              })
              refetch()
            },
            onError(error) {
              notification.push(getErrorResponse(error))
            },
          }
        )
      },
    })
  }

  const onAction = (actionType: any, row: any) => {
    if (actionType === 'delete') onDeleteIcon(row)
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'icon',
      header: 'ICON',
      cell: ({ row }) => (
        <img
          src={row.original.icon_url}
          alt={row.original.icon_name}
          className="w-8 h-8 object-contain"
        />
      ),
    },
    {
      accessorKey: 'icon_name',
      header: 'ICON NAME',
    },
    // {
    //   id: 'status',
    //   header: 'STATUS',
    //   accessorKey: 'status',
    //   cell: ({ row }) =>
    //     RenderStatus(row, 'status', onStatusChange),
    //   enableSorting: false,
    // },
    {
      accessorKey: 'createdAt',
      header: 'CREATED AT',
      cell: ({ row }) => {
        const value = row.original.createdAt
        if (!value) return '-'

        return new Date(value).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      },
    },
    {
      id: 'actions',
      header: 'ACTIONS',
      cell: ({ row }) =>
        RenderDefaultActions(row, ['delete'], onAction),
      enableSorting: false,
    }
  ]

  const rowSelectionState = useState<RowSelectionState>({})

  useEffect(() => {
    const subscriber = useFormHook.watch((values, { name }) => {
      if (!name) return

      const updated = getUpdatedSearchParams(filterParams, values, name)

      if (Object.keys(updated).length) {
        setSearchParams({ filter: encodeURIComponent(JSON.stringify(updated)) })
      } else {
        setSearchParams({})
      }
    })

    return () => subscriber.unsubscribe()
  }, [useFormHook, filterParams, setSearchParams])

  return isError || isSuccess ? (
    <SmoothTransition>
      {(error as any)?.response?.data?.statusCode === 403 ? (
        <Unauthorized />
      ) : (
        <>
          <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border shadow-md">
            <CustomTable
              title="Icon Master"
              formHook={useFormHook}
              columns={columns}
              data={responseData?.results}
              pages={responseData?.pages}
              totalRecords={responseData?.total}
              onRefresh={refetch}
              isLoading={isFetching}
              isErrored={isError}
              rowSelectionState={rowSelectionState}
              useFieldForSelect="id"
              onAddAction={() => setIsAddOpen(true)}
            />
          </div>

          <AddIconPopup
            isOpen={isAddOpen}
            setIsOpen={setIsAddOpen}
            onSubmitHandler={refetch}
          />
        </>
      )}
    </SmoothTransition>
  ) : (
    <PagePreloader />
  )
}

export default IconMasterListing
